import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  getBusinessPartners,
  getServicePartners,
} from "app/services/api/servicePartners";
import { FRONTEND_TYPE } from "app/utils/constants/settings";
import { getLicenseOptions } from "app/pages/price-calculator/helpers";
import config from "../../../../../config/licencePriceCalculator.json";
import {
  BACKEND_DOMAIN_OPTIONS,
  POS_INTEGRACIA_OPTIONS,
  RELATIONS_TYP_ZAKAZNIKA_OPTIONS,
} from "./step2Options";

const STEP_TITLE = "Informácie o licencii";

const BUSINESS_PARTNER_SIMPLE = { value: "1", label: "Simpleo" };
const SERVISNY_PARTNER_NONE = { value: "none", label: "Žiadny" };

const Step2LicenseInfo = ({ values, errors, onChange }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [businessPartners, setBusinessPartners] = useState([]);
  const [servicePartners, setServicePartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingPartners(true);
      try {
        const [businessList, serviceList] = await Promise.all([
          getBusinessPartners(),
          getServicePartners(),
        ]);
        if (!cancelled) {
          setBusinessPartners(Array.isArray(businessList) ? businessList : []);
          setServicePartners(Array.isArray(serviceList) ? serviceList : []);
        }
      } catch (err) {
        if (!cancelled) {
          setBusinessPartners([]);
          setServicePartners([]);
          enqueueSnackbar(
            t("pages.dsokDetails.servicePartnersLoadError", {
              defaultValue: "Nepodarilo sa načítať partnerov",
            }),
            { variant: "error" },
          );
        }
      } finally {
        if (!cancelled) setLoadingPartners(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, t]);

  const licenseOptions = useMemo(() => getLicenseOptions(config, null), []);

  const handleChange = (field) => (e) => {
    onChange(field, e.target.value);
  };

  const businessPartnerOptions = useMemo(() => {
    const seen = new Set();
    const fromApi = (businessPartners || [])
      .filter((item) => {
        const id = item?.id ?? item?._id;
        if (id == null || seen.has(id)) return false;
        if (Number(id) === 1) return false;
        seen.add(id);
        return true;
      })
      .sort((a, b) => {
        const na = String(a?.company_name ?? "").trim();
        const nb = String(b?.company_name ?? "").trim();
        return na.localeCompare(nb, undefined, { sensitivity: "base" });
      })
      .map((item) => ({
        value: String(item?.id ?? item?._id),
        label:
          String(item?.company_name ?? "").trim() ||
          String(item?.id ?? item?._id),
      }));
    return [BUSINESS_PARTNER_SIMPLE, ...fromApi];
  }, [businessPartners]);

  const servicePartnerOptions = useMemo(() => {
    const seen = new Set();
    const fromApi = (servicePartners || [])
      .filter((item) => {
        const id = item?.id ?? item?._id;
        if (id == null || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .sort((a, b) => {
        const na = String(a?.company_name ?? "").trim();
        const nb = String(b?.company_name ?? "").trim();
        return na.localeCompare(nb, undefined, { sensitivity: "base" });
      })
      .map((item) => ({
        value: String(item?.id ?? item?._id),
        label:
          String(item?.company_name ?? "").trim() ||
          String(item?.id ?? item?._id),
      }));
    return [SERVISNY_PARTNER_NONE, ...fromApi];
  }, [servicePartners]);

  if (loadingPartners) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {STEP_TITLE}
          </Typography>
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {STEP_TITLE}
        </Typography>
        <Grid container spacing={3} sx={{ pt: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.beDomain}>
              <InputLabel>Backend doména *</InputLabel>
              <Select
                value={values.beDomain ?? ""}
                onChange={handleChange("beDomain")}
                label="Backend doména *"
              >
                {BACKEND_DOMAIN_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.beDomain && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.beDomain}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.frontendType}>
              <InputLabel>Frontend type *</InputLabel>
              <Select
                value={values.frontendType ?? ""}
                onChange={handleChange("frontendType")}
                label="Frontend type *"
              >
                {FRONTEND_TYPE.map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.frontendType && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.frontendType}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.businessPartner}>
              <InputLabel>Obchodný partner *</InputLabel>
              <Select
                value={values.businessPartner ?? ""}
                onChange={handleChange("businessPartner")}
                label="Obchodný partner *"
                displayEmpty
              >
                <MenuItem value="">
                  <em>
                    {t("pages.dsokDetails.selectBusinessPartner", {
                      defaultValue: "Vyberte obchodného partnera",
                    })}
                  </em>
                </MenuItem>
                {businessPartnerOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.businessPartner && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.businessPartner}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.servicePartner}>
              <InputLabel>Servisný partner *</InputLabel>
              <Select
                value={values.servicePartner ?? "none"}
                onChange={handleChange("servicePartner")}
                label="Servisný partner *"
              >
                {servicePartnerOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.servicePartner && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.servicePartner}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.typLicencie}>
              <InputLabel>Typ licencie *</InputLabel>
              <Select
                value={values.typLicencie ?? ""}
                onChange={handleChange("typLicencie")}
                label="Typ licencie *"
              >
                {licenseOptions.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.typLicencie && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.typLicencie}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.relationsTypZakaznika}>
              <InputLabel>Relations – typ zákazníka *</InputLabel>
              <Select
                value={values.relationsTypZakaznika ?? ""}
                onChange={handleChange("relationsTypZakaznika")}
                label="Relations – typ zákazníka *"
              >
                {RELATIONS_TYP_ZAKAZNIKA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.relationsTypZakaznika && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.relationsTypZakaznika}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.posIntegracia}>
              <InputLabel>POS integrácia *</InputLabel>
              <Select
                value={values.posIntegracia ?? "none"}
                onChange={handleChange("posIntegracia")}
                label="POS integrácia *"
              >
                {POS_INTEGRACIA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.posIntegracia && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.posIntegracia}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default Step2LicenseInfo;
